import { Element, RectCenteredElement } from '@thive/canvas';
import { Station } from '@thive/fddm-types';

import { theme } from '@modules/common/theme';
import { Z_INDEX_MAP } from '@/modules/canvas/constants/elements';
import { elementFactory } from '@/modules/canvas';
import { NumUtils } from '@/modules/common/utils';
import { FDDM_TAG, INTERACTIVITY_OFF } from '@/modules/floorplanDesign/constants';
import { modifyColor } from '@/modules/floorplanDesign/mappers/colors';

type StationsByPosition = {
  stationsToRender: Station[];
  duplicateStationIds: string[];
};

const getStationPositionKey = (station: Station): string => {
  const { centerX, centerY } = station.cuboid.rectangle;

  return `${NumUtils.metersToMmRounded(centerX)}:${NumUtils.metersToMmRounded(centerY)}`;
};

const groupStationsByPosition = (stations: readonly Station[]): StationsByPosition => {
  const occupiedPositions = new Set<string>();
  const stationsToRender: Station[] = [];
  const duplicateStationIds: string[] = [];

  stations.forEach((station) => {
    const positionKey = getStationPositionKey(station);

    if (occupiedPositions.has(positionKey)) {
      duplicateStationIds.push(station.id);
      return;
    }

    occupiedPositions.add(positionKey);
    stationsToRender.push(station);
  });

  return {
    stationsToRender,
    duplicateStationIds,
  };
};

export const getDuplicateStationIds = (stations: readonly Station[]): string[] =>
  groupStationsByPosition(stations).duplicateStationIds;

export const createStationElement = (station: Station, visible = true): RectCenteredElement =>
  elementFactory.buildRectangleElement({
    id: station.id,
    rectangle: station.cuboid.rectangle,
    z: Z_INDEX_MAP[FDDM_TAG.STATION],
    color: modifyColor(theme.palette.areas.loadBoxColor, true),
    interactivity: INTERACTIVITY_OFF,
    tags: [FDDM_TAG.STATION, FDDM_TAG.LAYER_FLOORPLAN],
    visible,
  });

export const stationMapper = (stations: Station[], visible = true): Element[] =>
  groupStationsByPosition(stations).stationsToRender.map((station) =>
    createStationElement(station, visible),
  );
